import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface UseTaskEventsOptions {
	transport?: "polling" | "sse";
	intervalMs?: number;
}

interface UseTaskEventsResult {
	done: boolean;
	error: Error | null;
	connectionLost: boolean;
}

const DEFAULT_INTERVAL_MS = 500;
const CONNECTION_LOST_THRESHOLD = 3;
const TOAST_ID = "task-connection-lost";
// Sprint 6 / C5.1: exp-backoff для polling.
const MAX_ERR_COUNT = 10; // cap счётчика ошибок (без cap'а — 2^N бесконечно)
const MAX_BACKOFF_MS = 30_000; // максимальная пауза между попытками
const CONNECTION_LOST_TOAST_MS = 30_000; // duration toast'а (раньше Infinity)

/**
 * React-хук для получения событий задачи через polling (или SSE).
 *
 * @param taskId — ID задачи (null = не запускать polling)
 * @param onEvent — колбэк для каждого события
 * @param options — transport и intervalMs
 */
export function useTaskEvents(
	taskId: string | null,
	onEvent: (event: string, data: unknown) => void,
	options?: UseTaskEventsOptions,
): UseTaskEventsResult {
	const transport = options?.transport ?? import.meta.env.VITE_TASK_TRANSPORT ?? "polling";
	const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

	const cursorRef = useRef(-1);
	const errCountRef = useRef(0);
	const onEventRef = useRef(onEvent);
	onEventRef.current = onEvent;

	const [done, setDone] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [connectionLost, setConnectionLost] = useState(false);

	useEffect(() => {
		if (!taskId) return;

		if (transport === "sse") {
			// SSE transport
			const controller = new AbortController();

			(async () => {
				try {
					const res = await fetch(`/api/tasks/${taskId}/stream?after=${cursorRef.current}`, {
						signal: controller.signal,
					});
					if (!res.ok) {
						setError(new Error(`SSE: ${res.statusText}`));
						return;
					}

					const reader = res.body?.getReader();
					if (!reader) return;

					const decoder = new TextDecoder();
					let buffer = "";
					let currentEvent = "";

					while (true) {
						const { done: readerDone, value } = await reader.read();
						if (readerDone) break;

						buffer += decoder.decode(value, { stream: true });
						const lines = buffer.split("\n");
						buffer = lines.pop() || "";

						for (const line of lines) {
							if (line.startsWith("event: ")) {
								currentEvent = line.slice(7).trim();
							} else if (line.startsWith("data: ")) {
								try {
									const data = JSON.parse(line.slice(6));
									onEventRef.current(currentEvent, data);
								} catch {
									// ignore parse errors
								}
								currentEvent = "";
							}
						}
					}

					setDone(true);
				} catch (err) {
					if (controller.signal.aborted) return;
					setError(err instanceof Error ? err : new Error(String(err)));
				}
			})();

			return () => controller.abort();
		}

		// Polling transport (default).
		// Sprint 6 / C5.1: переключились с setInterval на recursive setTimeout,
		// чтобы реализовать exp-backoff на ошибках. Успешный ответ сбрасывает
		// счётчик и возвращает обычный intervalMs; ошибка — pause = intervalMs *
		// 2^errCount, max 30с. errCount cap'нут на 10, чтобы не дёргать
		// `Number.MAX_SAFE_INTEGER` shifts. Toast «connection-lost» теперь
		// duration: 30с (не Infinity — у пользователя должно быть окно
		// «починилось» без ручного dismiss).
		const controller = new AbortController();
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const scheduleNext = (delay: number) => {
			if (controller.signal.aborted) return;
			timeoutId = setTimeout(poll, delay);
		};

		const poll = async () => {
			try {
				const res = await fetch(`/api/tasks/${taskId}?after=${cursorRef.current}`, {
					signal: controller.signal,
				});
				if (!res.ok) throw new Error(res.statusText);
				const data = await res.json();

				if (controller.signal.aborted) return;

				// Успех — сброс счётчика + dismiss toast'а.
				if (errCountRef.current > 0) {
					errCountRef.current = 0;
					setConnectionLost(false);
					toast.dismiss(TOAST_ID);
				}

				for (const evt of data.events) {
					onEventRef.current(evt.event, evt.data);
				}
				cursorRef.current = data.cursor;

				if (data.done) {
					setDone(true);
					return; // не schedule, очередь пуста
				}
				scheduleNext(intervalMs);
			} catch {
				if (controller.signal.aborted) return;
				errCountRef.current = Math.min(errCountRef.current + 1, MAX_ERR_COUNT);
				if (errCountRef.current >= CONNECTION_LOST_THRESHOLD) {
					setConnectionLost(true);
					toast.error("Проблема с подключением", {
						id: TOAST_ID,
						duration: CONNECTION_LOST_TOAST_MS,
					});
				}
				const backoff = Math.min(intervalMs * 2 ** errCountRef.current, MAX_BACKOFF_MS);
				scheduleNext(backoff);
			}
		};

		// Initial poll — без задержки.
		scheduleNext(intervalMs);

		return () => {
			controller.abort();
			if (timeoutId !== null) clearTimeout(timeoutId);
		};
	}, [taskId, transport, intervalMs]);

	return { done, error, connectionLost };
}
